import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  disclosureTitle,
  SubscriptionDisclosure,
} from '../../../SubscriptionDisclosure';

// Inline onboarding variant of the Subscription Disclosure: one section per
// plan or subscription the account already carries (partner paid-in-office
// orders) or is about to receive (free-trial referral). Each section binds to
// subscriptionAcknowledgments[index] in the onboarding form so the whole
// thing submits with createOnboarding.
function OnboardingFormSubscriptions({ disclosure }) {
  const { setValue, getValues } = useFormContext();
  const acknowledgments = useWatch({ name: 'subscriptionAcknowledgments' });
  const {
    formState: { errors },
  } = useFormContext();

  const tiers = useMemo(
    () => [...(disclosure?.tiers || [])].sort((a, b) => a.price - b.price),
    [disclosure?.tiers],
  );

  function submissionAt(index) {
    return (
      acknowledgments?.[index] || {
        agreed: false,
        selectedSubscriptionProductId: null,
        lowerTiersExpanded: false,
      }
    );
  }

  function updateAt(index, submission) {
    const current = getValues('subscriptionAcknowledgments') || [];
    const next = [...current];
    next[index] = { ...next[index], ...submission };
    setValue('subscriptionAcknowledgments', next, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }

  function errorsAt(index) {
    const fieldErrors = errors?.subscriptionAcknowledgments?.[index];
    if (!fieldErrors) {
      return null;
    }

    return Object.entries(fieldErrors)
      .filter(([, error]) => error?.message)
      .map(([path, error]) => ({ path, message: error.message }));
  }

  return (
    <div className="flex flex-col gap-10">
      {(disclosure?.items || []).map((item, index) => (
        <section
          key={item.key}
          className="flex flex-col gap-4 rounded-lg border border-rcprimary-400/40 bg-white p-4 sm:p-6"
        >
          <h4 className="font-semibold text-gray-950 text-xl">
            {disclosureTitle({ product: item.product, patient: item.patient })}
          </h4>
          <SubscriptionDisclosure
            product={item.product}
            tiers={tiers}
            submission={{
              agreed: submissionAt(index).agreed === true,
              selectedSubscriptionProductId:
                submissionAt(index).selectedSubscriptionProductId || null,
              lowerTiersExpanded:
                submissionAt(index).lowerTiersExpanded === true,
            }}
            onChange={(submission) => updateAt(index, submission)}
            errorMessages={errorsAt(index)}
          />
        </section>
      ))}
    </div>
  );
}

OnboardingFormSubscriptions.propTypes = {
  disclosure: PropTypes.object,
};

export default OnboardingFormSubscriptions;
